import axios from "axios";
import camelCase from "lodash.camelcase";

import { TFetcherConfig, TFetcherError } from "@type/helpers/fetcher";
import { logout } from "helpers/logout";
import { transformer } from "helpers/transformer";
import { refreshToken } from "lib/axios/refreshToken";
import { getCookies } from "lib/cookies";

import { API_HOST, API_PATH, MAIN_URL } from "config";
import { API_HEADER } from "@type/api/constants";
import { ENDPOINT } from "@type/api/endpoint";
import {
  AUTH_COOKIES_KEY,
  ERROR_NON_LOGOUT_MESSAGES,
  TOKEN_EXPIRED_MESSAGES,
} from "constants/index";

const _axios = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

_axios.interceptors.request.use(
  (config) => {
    const token = getCookies(AUTH_COOKIES_KEY.TOKEN);

    if (token) {
      config.headers["Authorization"] = token;
    }
    if (!config.baseURL) {
      const version = config.headers?.[API_HEADER.VERSION] || "";
      config.baseURL = `${API_HOST}${version}${API_PATH}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

_axios.interceptors.response.use(
  async (res) => {
    const ignoredKeys =
      (res.config as TFetcherConfig<any>).ignoredKeysRes || [];
    if (res?.data) {
      res.data = transformer(res.data, camelCase, ignoredKeys);
    }
    return res;
  },
  async (err) => {
    const originalConfig = err.config;
    if (err.response) {
      // Response unauthorized
      if (err.response.status === 401) {
        if (
          TOKEN_EXPIRED_MESSAGES.includes(
            err?.response?.data?.message?.toLowerCase()
          ) &&
          !originalConfig._retry
        ) {
          // Access token was expired
          originalConfig._retry = true;
          try {
            const newAccessToken = await refreshToken();
            originalConfig.headers["Authorization"] = newAccessToken;
            return _axios(originalConfig);
          } catch (_error) {
            return logout();
          }
        } else if (
          ERROR_NON_LOGOUT_MESSAGES.includes(
            err?.response?.data?.message?.toLowerCase()
          ) ||
          originalConfig?.url === ENDPOINT.USER.LOGIN.url
        ) {
          return Promise.reject({
            message:
              err?.response?.data?.message ||
              err?.message ||
              "Unexpected error",
            code: err?.code,
          });
        } else {
          return logout();
        }
      } else if (
        err.response.status === 404 &&
        originalConfig?.headers?.[API_HEADER.BLOCK_404] === "true"
      ) {
        window.location.href = `${MAIN_URL}/404-notfound`;
      } else {
        const errorObject: TFetcherError = {
          message:
            err?.response?.data?.message || err?.message || "Unexpected error",
          code: err?.code,
          data: err?.response?.data,
          payload: {
            baseUrl: originalConfig?.baseURL,
            path: originalConfig?.url,
            data: originalConfig?.data,
            params: JSON.stringify(originalConfig?.params || {}),
          },
        };
        return Promise.reject(errorObject);
      }
    }

    return Promise.reject(err);
  }
);

export default _axios;
